import axios from 'axios';
import various from '../General/various';

const ORIGIN = 'https://www.touzen.jp';
const profileSizes = { share: [1200, 630], search_wide: [1200, 675], search_landscape: [1200, 900], search_square: [1200, 1200] };
const failure = (kind) => Object.assign(new Error('TOUZEN_BLOG_SEO_UNAVAILABLE'), { kind });
const articlePath = (path) => {
  if (typeof path !== 'string' || !path.startsWith('/blog/posts/')) return false;
  const slug = path.slice('/blog/posts/'.length);
  return slug.length > 0 && slug.length <= 255 && !/[^-a-zA-Z0-9_]/.test(slug);
};
const validDate = (value) => value === null || (typeof value === 'string' && value.length > 0 && Number.isFinite(Date.parse(value)));
const validImageUrl = (value) => {
  try { const url = new URL(value); return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password && !url.hash; } catch { return false; }
};
const canonical = (path) => `${ORIGIN}${path}`;
const validAuthorUrl = (value) => {
  try {
    const url = new URL(value);
    return url.origin === ORIGIN && url.pathname === '/blog' && !url.hash
      && [...url.searchParams.keys()].join(',') === 'author' && /^[a-f0-9]{64}$/.test(url.searchParams.get('author'));
  } catch { return false; }
};

const normalizeRoute = (data, path) => {
  const root = path === '/blog';
  const schema = root ? 'CollectionPage' : 'BlogPosting';
  if (!data || data.canonical_path !== path || data.canonical_url !== canonical(path) || data.indexable !== true
    || ['seo_title', 'seo_description', 'heading', 'body', 'author_name'].some((field) => typeof data[field] !== 'string')
    || data.schema_type !== schema || !Array.isArray(data.schema_types) || data.schema_types.length !== 1 || data.schema_types[0] !== schema
    || !validDate(data.published_at) || !validDate(data.modified_at) || (data.image_alt !== undefined && typeof data.image_alt !== 'string')
    || (data.author_url !== undefined && !validAuthorUrl(data.author_url))
    || !Array.isArray(data.keywords) || data.keywords.some((name) => typeof name !== 'string' || !name.trim())
    || !Array.isArray(data.collection_items) || data.collection_items.length > 10 || (!root && data.collection_items.length > 0)) throw failure('invalid_response');
  const imageProfiles = {};
  for (const [name, [width, height]] of Object.entries(profileSizes)) {
    const image = data.image_profiles?.[name];
    if (!image || typeof image.url !== 'string' || !validImageUrl(image.url)
      || image.width !== width || image.height !== height || image.type !== 'image/jpeg') throw failure('invalid_response');
    imageProfiles[name] = { url: image.url, width, height, type: image.type };
  }
  if (data.og_image !== imageProfiles.share.url) throw failure('invalid_response');
  const items = data.collection_items.map((item) => {
    let url;
    try { url = new URL(item?.url); } catch { throw failure('invalid_response'); }
    if (url.origin !== ORIGIN || !articlePath(url.pathname) || item.url !== canonical(url.pathname)
      || ['title', 'description', 'author_name'].some((field) => typeof item[field] !== 'string') || !validDate(item.published_at)) throw failure('invalid_response');
    return { title: item.title, url: item.url, description: item.description, author_name: item.author_name, published_at: item.published_at };
  });
  return { canonical_path: path, canonical_url: canonical(path), indexable: true, seo_title: data.seo_title,
    seo_description: data.seo_description, heading: data.heading, body: data.body, author_name: data.author_name,
    ...(data.author_url ? { author_url: data.author_url } : {}), published_at: data.published_at, modified_at: data.modified_at,
    keywords: [...data.keywords], schema_type: schema, schema_types: [schema], image_profiles: imageProfiles,
    og_image: imageProfiles.share.url, image_alt: data.image_alt || '刀禅ブログ', collection_items: items };
};

export async function resolveBlogSeo(path, signal) {
  if (path !== '/blog' && !articlePath(path || '')) throw failure('validation');
  let data;
  try {
    // Anonymous fetch transport bypasses inherited auth, cookies and global interceptors.
    const response = await axios.create().request({ method: 'get', url: `${various.currentDomain.replace(/\/?$/, '/')}api/seo/resolve/`,
      params: { path }, adapter: 'fetch', withCredentials: false, withXSRFToken: false, auth: null,
      headers: { Authorization: null, Cookie: null }, timeout: 15000, signal });
    data = response.data;
  } catch (error) {
    const status = error?.response?.status;
    throw failure(status === 404 ? 'not_found' : error?.code === 'ERR_CANCELED' ? 'canceled'
      : ['ECONNABORTED', 'ETIMEDOUT'].includes(error?.code) ? 'timeout' : status ? 'unavailable' : 'network');
  }
  return normalizeRoute(data, path);
}
